import React from 'react';
import { parsePhoneNumber, getCountryCallingCode, CountryCode } from 'libphonenumber-js';
import Flag from 'react-world-flags';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getCountryName } from '@/lib/countryUtils';

interface PhoneNumberDisplayProps {
  phoneNumber: string;
  className?: string;
  showFlag?: boolean;
  showCountryCode?: boolean;
}

export const PhoneNumberDisplay: React.FC<PhoneNumberDisplayProps> = ({
  phoneNumber,
  className = '',
  showFlag = true,
  showCountryCode = true,
}) => {
  try {
    // Parse the phone number
    const parsedNumber = parsePhoneNumber(phoneNumber);
    
    if (!parsedNumber || !parsedNumber.isValid()) {
      // If parsing fails or number is invalid, return the original
      return <span className={className}>{phoneNumber}</span>;
    }

    // Get the country code
    const countryCode = parsedNumber.country;
    const countryCallingCode = parsedNumber.countryCallingCode;
    let nationalNumber = parsedNumber.formatNational();
    
    // Remove leading zeros when country code is shown
    if (showCountryCode && nationalNumber.startsWith('0')) {
      nationalNumber = nationalNumber.substring(1);
    }

    return (
      <TooltipProvider>
        <span className={`inline-flex items-center gap-2 ${className}`}>
          {showFlag && countryCode && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center cursor-help">
                  <Flag 
                    code={countryCode} 
                    style={{ width: '1.25rem', height: '0.875rem' }}
                    fallback={<span className="text-xs text-muted-foreground">{countryCode}</span>}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {getCountryName(countryCode)}
              </TooltipContent>
            </Tooltip>
          )}
          {showCountryCode && (
            <span className="text-muted-foreground">+{countryCallingCode}</span>
          )}
          <span>{nationalNumber}</span>
        </span>
      </TooltipProvider>
    );
  } catch (error) {
    // If there's any error in parsing, return the original number
    console.warn('Error parsing phone number:', error);
    return <span className={className}>{phoneNumber}</span>;
  }
};